import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useConfig } from "../context/ConfigContext";
import { toast } from "sonner";
import { History, Plus, Trash2 } from "lucide-react";

const STATUS_COLOR = { Completed: "#10B981", "Published/Sent": "#10B981", Planned: "#94A3B8", Ready: "#3B82F6", Skipped: "#64748B" };
const PILLAR_LABELS = { DREAM: "Dream", PRODUCT_PROOF: "Product Proof", VALUE: "Value", AFFORDABILITY: "Affordability", COMMUNITY: "Community", TRUST: "Trust", FAQ: "FAQ" };
const BLANK = { action_type: "Facebook post", platform: "", audience: "", pillar: "DREAM", theme: "", status: "Completed", date: "", notes: "" };

export default function ActivityHistory() {
  const { config } = useConfig();
  const [acts, setActs] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(BLANK);

  const load = () => api.activities().then(setActs);
  useEffect(() => { load(); }, []);

  const save = async () => {
    await api.createActivity({ ...form, date: form.date || null, source: "manual" });
    toast.success("Activity logged"); setForm(BLANK); setShow(false); load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div><div className="flex items-center gap-2 eyebrow text-[#D4AF37]"><History className="w-4 h-4" /> Campaign Activity Ledger</div><h1 className="font-display text-3xl sm:text-4xl mt-1">Everything we've done</h1></div>
        <button data-testid="add-activity-btn" onClick={() => setShow((s) => !s)} className="gold-btn px-4 py-2.5 rounded-lg text-sm inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Log activity</button>
      </div>

      {show && (
        <div className="card-lux p-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="activity-form">
          <select value={form.action_type} onChange={(e) => setForm({ ...form, action_type: e.target.value })} className="px-3 py-2.5 rounded-lg bg-[#0A0C10] border border-[#2A3042] text-sm outline-none focus:border-[#D4AF37]">
            {(config?.action_types || []).map((a) => <option key={a}>{a}</option>)}
          </select>
          <select value={form.pillar} onChange={(e) => setForm({ ...form, pillar: e.target.value })} className="px-3 py-2.5 rounded-lg bg-[#0A0C10] border border-[#2A3042] text-sm outline-none focus:border-[#D4AF37]">
            {Object.keys(PILLAR_LABELS).map((p) => <option key={p} value={p}>{PILLAR_LABELS[p]}</option>)}
          </select>
          <select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} className="px-3 py-2.5 rounded-lg bg-[#0A0C10] border border-[#2A3042] text-sm outline-none focus:border-[#D4AF37]">
            <option value="">Audience…</option>{(config?.audiences || []).map((a) => <option key={a}>{a}</option>)}
          </select>
          <input placeholder="Theme" value={form.theme} onChange={(e) => setForm({ ...form, theme: e.target.value })} className="px-3 py-2.5 rounded-lg bg-[#0A0C10] border border-[#2A3042] text-sm outline-none focus:border-[#D4AF37]" />
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="px-3 py-2.5 rounded-lg bg-[#0A0C10] border border-[#2A3042] text-sm outline-none focus:border-[#D4AF37]" />
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="px-3 py-2.5 rounded-lg bg-[#0A0C10] border border-[#2A3042] text-sm outline-none focus:border-[#D4AF37]">
            {(config?.statuses || []).map((s) => <option key={s}>{s}</option>)}
          </select>
          <input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="px-3 py-2.5 rounded-lg bg-[#0A0C10] border border-[#2A3042] text-sm outline-none focus:border-[#D4AF37] sm:col-span-2" />
          <button data-testid="save-activity-btn" onClick={save} className="gold-btn rounded-lg text-sm py-2.5">Save</button>
        </div>
      )}

      <div className="card-lux p-5 overflow-x-auto">
        {acts.length === 0 ? <p className="text-sm text-[#64748B]">No activity yet. Confirm your history in "What We've Done", or log an activity.</p> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-[#64748B] uppercase tracking-wider border-b border-[#2A3042]">
              <th className="py-2 pr-3">Date</th><th className="pr-3">Action</th><th className="pr-3">Pillar</th><th className="pr-3">Theme</th><th className="pr-3">Audience</th><th className="pr-3">Status</th><th></th>
            </tr></thead>
            <tbody>
              {acts.map((a) => (
                <tr key={a.id} className="border-b border-[#2A3042]/60" data-testid={`activity-${a.id}`}>
                  <td className="py-2.5 pr-3 text-[#94A3B8] font-mono2 text-xs">{(a.date || a.created_at || "").slice(0, 10)}</td>
                  <td className="pr-3 text-[#F8FAFC]">{a.action_type}</td>
                  <td className="pr-3 text-[#94A3B8]">{PILLAR_LABELS[a.pillar] || a.pillar || "—"}</td>
                  <td className="pr-3 text-[#94A3B8]">{a.theme || "—"}</td>
                  <td className="pr-3 text-[#94A3B8]">{a.audience || "—"}</td>
                  <td className="pr-3"><span className="text-xs px-2 py-0.5 rounded" style={{ background: (STATUS_COLOR[a.status] || "#666") + "22", color: STATUS_COLOR[a.status] || "#999" }}>{a.status}</span></td>
                  <td className="text-right"><button onClick={() => api.delActivity(a.id).then(load)} className="text-[#64748B] hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
